import { Link } from 'react-router-dom';

import { Badge, Button, Icon, ProgressBar } from '@/components/ui';
import { paths } from '@/routes/paths';

/**
 * The roadmap from a goal to a first internship, as returned by
 * `POST /students/me/career-roadmap`.
 *
 * Two kinds of statement sit side by side here and are never allowed to blur.
 * The stages, the "why" lines and the projects are a plan -- labelled
 * "AI-suggested" when a model wrote them. The listing counts and the "on your
 * profile" marks are facts from the platform's own rows, and are shown only
 * where the backend could tie a skill to one.
 */
export function Roadmap({ roadmap, onKnowSkill, knowingSkill, onRefresh, isRefreshing }) {
  const percent = roadmap.skills_total
    ? Math.round((roadmap.skills_have / roadmap.skills_total) * 100)
    : 0;
  const isAi = roadmap.source === 'ai';

  return (
    <div>
      <div className="flex flex-wrap items-center gap-2">
        {isAi ? (
          <Badge tone="brand">AI-suggested plan</Badge>
        ) : (
          <Badge>Counted from open listings</Badge>
        )}
        <span className="text-ink-muted text-xs">
          Grounded in {roadmap.open_listings} open listing(s) in {roadmap.category.name}
        </span>
        {isAi && onRefresh && (
          <Button
            variant="ghost"
            size="sm"
            className="ml-auto"
            isLoading={isRefreshing}
            onClick={onRefresh}
          >
            New take
          </Button>
        )}
      </div>

      {roadmap.summary && <p className="text-ink mt-4 text-sm leading-6">{roadmap.summary}</p>}

      {!isAi && (
        <p className="text-ink-muted mt-3 text-sm">
          A step-by-step plan could not be written right now, so this is what open listings in the
          field ask for, most-requested first.
        </p>
      )}

      {roadmap.skills_total > 0 && (
        <ProgressBar
          className="mt-5"
          value={percent}
          label={`${roadmap.skills_have} of ${roadmap.skills_total} skills on your profile`}
        />
      )}

      <ol className="mt-6 space-y-0">
        {roadmap.stages.map((stage, index) => (
          <Stage
            key={`${index}-${stage.title}`}
            stage={stage}
            number={index + 1}
            isLast={index === roadmap.stages.length - 1}
            onKnowSkill={onKnowSkill}
            knowingSkill={knowingSkill}
          />
        ))}
      </ol>

      {roadmap.also_asked_for.length > 0 && (
        <div className="border-line mt-6 border-t pt-5">
          <h3 className="text-ink text-sm font-semibold">Also asked for by listings</h3>
          <p className="text-ink-muted mt-0.5 text-xs">
            Open listings in this field ask for these too. Fit them in where they make sense.
          </p>
          <SkillList
            skills={roadmap.also_asked_for}
            onKnowSkill={onKnowSkill}
            knowingSkill={knowingSkill}
          />
        </div>
      )}

      {roadmap.tips.length > 0 && (
        <div className="bg-brand-50/60 rounded-card mt-6 px-5 py-4">
          <h3 className="text-ink text-sm font-semibold">Landing the internship</h3>
          <ul className="mt-2 space-y-1.5">
            {roadmap.tips.map((tip) => (
              <li key={tip} className="text-ink-muted flex gap-2 text-sm">
                <Icon name="check" size={15} className="text-brand-600 mt-0.5 shrink-0" />
                {tip}
              </li>
            ))}
          </ul>
        </div>
      )}

      {roadmap.internships.length > 0 && (
        <div className="border-line mt-6 border-t pt-5">
          <h3 className="text-ink text-sm font-semibold">Where this leads</h3>
          <p className="text-ink-muted mt-0.5 text-xs">
            Open internships in this field, by how much of what they ask for this plan covers.
          </p>
          <ul className="mt-3 grid gap-2 sm:grid-cols-2">
            {roadmap.internships.map((internship) => (
              <li key={internship.id}>
                <Link
                  to={paths.internshipDetail(internship.slug || internship.id)}
                  className="border-line hover:border-brand-200 hover:bg-brand-50/40 rounded-card flex items-center justify-between gap-3 border px-4 py-3 transition"
                >
                  <span className="min-w-0">
                    <span className="text-ink block truncate text-sm font-medium">
                      {internship.title}
                    </span>
                    <span className="text-ink-muted block truncate text-xs">
                      {internship.company_name}
                    </span>
                  </span>
                  {internship.total_skills > 0 && (
                    <Badge
                      tone={
                        internship.matched_skills === internship.total_skills
                          ? 'success'
                          : 'neutral'
                      }
                    >
                      Covers {internship.matched_skills}/{internship.total_skills}
                    </Badge>
                  )}
                </Link>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

function Stage({ stage, number, isLast, onKnowSkill, knowingSkill }) {
  return (
    <li className="relative flex gap-4 pb-6 last:pb-0">
      {/* The rail joining the stages; stops at the last one. */}
      {!isLast && (
        <span aria-hidden="true" className="bg-brand-100 absolute top-9 bottom-0 left-4 w-px" />
      )}
      <span className="bg-brand-600 relative grid size-8 shrink-0 place-items-center rounded-full text-sm font-semibold text-white shadow-sm">
        {number}
      </span>

      <div className="min-w-0 flex-1 pt-0.5">
        <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
          <h3 className="text-ink text-[15px] font-semibold">{stage.title}</h3>
          {stage.duration_weeks && (
            <span className="text-ink-muted text-xs">
              about {stage.duration_weeks} week{stage.duration_weeks === 1 ? '' : 's'}
            </span>
          )}
        </div>
        {stage.outcome && <p className="text-ink-muted mt-0.5 text-sm">{stage.outcome}</p>}

        <SkillList skills={stage.skills} onKnowSkill={onKnowSkill} knowingSkill={knowingSkill} />

        {stage.project && (
          <div className="border-brand-200/70 bg-brand-50/40 rounded-card mt-3 border border-dashed px-4 py-3">
            <p className="text-brand-700 text-[11px] font-semibold tracking-wide uppercase">
              Build this
            </p>
            <p className="text-ink mt-0.5 text-sm font-medium">{stage.project.title}</p>
            {stage.project.description && (
              <p className="text-ink-muted mt-0.5 text-sm">{stage.project.description}</p>
            )}
          </div>
        )}
      </div>
    </li>
  );
}

function SkillList({ skills, onKnowSkill, knowingSkill }) {
  return (
    <ul className="divide-line mt-3 divide-y">
      {skills.map((skill) => (
        <li key={skill.name} className="flex flex-wrap items-start justify-between gap-3 py-2.5">
          <div className="min-w-0 flex-1 basis-56">
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-ink text-sm font-medium">{skill.name}</span>
              {skill.listings > 0 && (
                <Badge tone="warning">
                  Asked in {skill.listings} listing{skill.listings === 1 ? '' : 's'}
                  {skill.required_in > 0 && ` · required in ${skill.required_in}`}
                </Badge>
              )}
            </div>
            {skill.why && <p className="text-ink-muted mt-0.5 text-xs">{skill.why}</p>}
          </div>
          {skill.already_have ? (
            <Badge tone="success">On your profile</Badge>
          ) : (
            onKnowSkill && (
              // Green and bordered: it turns into the green "On your profile"
              // badge, and a borderless ghost vanished down a long list.
              <Button
                variant="tintPositive"
                size="sm"
                className="shrink-0 font-semibold"
                isLoading={knowingSkill === skill.name}
                onClick={() => onKnowSkill(skill.name)}
                title={`Add ${skill.name} to your profile`}
              >
                <Icon name="plus" size={14} />I know this
              </Button>
            )
          )}
        </li>
      ))}
    </ul>
  );
}
